import {
  DeleteOutlined,
  PlusCircleOutlined,
  PlusCircleTwoTone,
  RollbackOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Typography,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import React, { useMemo } from "react";
import { ProductAttributeSelector } from "shared/components/product-attribute/product-attr-selector";
import { ProductAttributeValueSelector } from "shared/components/product-attribute/product-attr-value-selector";
import { QuestionRemove } from "shared/constants/define-localization";
import { CoreResource, t } from "shared/plugins/localization";
import {
  ProductAttributeModel,
  ProductAttributeValueModel,
} from "states/product-attribute/product-attribute.models";
import {
  LogicalTermEnum,
  ProductCategoryAttributeModel,
  ProductCategoryFilterSettingModel,
  ProductCategoryProductAttributeValueModel,
  ProductFilterSetting,
  ProductPriceFilterModel,
} from "states/product-category/product-category.models";

interface IProductCategoryFilterSettingProps {
  model: ProductFilterSetting;
  onChange: (model: ProductCategoryFilterSettingModel) => void;
  isUpdateMode: boolean;
}

export function ProductCategoryFilterSetting({
  model,
  isUpdateMode,
  onChange,
}: IProductCategoryFilterSettingProps) {
  const memorizedFilterSetting = useMemo(() => {
    return {
      priceFilters: model?.priceFilters ?? [],
      attributeFilters:
        model?.attributeFilters?.map((att) => {
          return {
            logicalTerm: att.logicalTerm,
            productAttributeId: att.productAttributeId,
            values: att.productAttributeValueIds?.map((attValue) => {
              return {
                valueId: attValue,
              } as ProductCategoryProductAttributeValueModel;
            }),
          };
        }) ?? [],
    } as ProductCategoryFilterSettingModel;
  }, [model]);
  //#region Attribute
  const handleAddAttribute = () => {
    let attributeTemp = getAttributeTemp();
    attributeTemp.push({} as ProductCategoryAttributeModel);
    onChange({ ...memorizedFilterSetting, attributeFilters: attributeTemp });
  };
  const handleDeleteAttribute = (index: number) => {
    let attributeTemp = getAttributeTemp();
    attributeTemp.splice(index, 1);
    onChange({ ...memorizedFilterSetting, attributeFilters: attributeTemp });
  };
  const getAttributeTemp = () => {
    return [...memorizedFilterSetting?.attributeFilters];
  };
  //#endregion

  //#region Attribute value
  const handleAddAttributeValue = (index: number) => {
    let attributeTemp = getAttributeTemp();
    if (!attributeTemp[index].values) {
      attributeTemp[index].values = [];
    }
    attributeTemp[index].values.push(
      {} as ProductCategoryProductAttributeValueModel
    );
    onChange({ ...memorizedFilterSetting, attributeFilters: attributeTemp });
  };
  //#endregion


  //#region Price Filter
  const handleAddPriceFilter = () => {
    let priceFiltersTemp = getPriceFilterTemp();
    priceFiltersTemp.push({} as ProductPriceFilterModel);
    onChange({ ...memorizedFilterSetting, priceFilters: priceFiltersTemp });
  };
  const handleDeletePriceFilter = (index: number) => {
    let priceFiltersTemp = getPriceFilterTemp();
    priceFiltersTemp.splice(index, 1);
    onChange({ ...memorizedFilterSetting, priceFilters: priceFiltersTemp });
  };
  const getPriceFilterTemp = () => {
    return [...memorizedFilterSetting.priceFilters];
  };
  const priceFilterColumns: ColumnsType<ProductPriceFilterModel> = [
    {
      title: "Display name",
      dataIndex: "displayedText",
      key: "displayedText",
      render: (displayedText: string, item, index) => (
        <React.Fragment>
          <Input
            value={displayedText}
            placeholder="Display name"
            onChange={(ev) => {
              let priceFiltersTemp = getPriceFilterTemp();
              priceFiltersTemp[index].displayedText = ev.target.value;
              onChange({
                ...memorizedFilterSetting,
                priceFilters: priceFiltersTemp,
              });
            }}
          />
        </React.Fragment>
      ),
    },
    {
      title: "Price From",
      dataIndex: "priceFrom",
      key: "priceFrom",
      render: (priceFrom: number, record, index) => (
        <React.Fragment>
          <Input
            value={priceFrom}
            placeholder="Price From"
            onChange={(ev) => {
              let priceFiltersTemp = getPriceFilterTemp();
              priceFiltersTemp[index].priceFrom = Number(ev.target.value);
              onChange({
                ...memorizedFilterSetting,
                priceFilters: priceFiltersTemp,
              });
            }}
          />
        </React.Fragment>
      ),
    },
    {
      title: "Price To",
      dataIndex: "priceTo",
      key: "priceTo",
      render: (priceTo: number, record, index) => (
        <React.Fragment>
          <Input
            value={priceTo}
            placeholder="Price To"
            onChange={(ev) => {
              let priceFiltersTemp = getPriceFilterTemp();
              priceFiltersTemp[index].priceTo = Number(ev.target.value);
              onChange({
                ...memorizedFilterSetting,
                priceFilters: priceFiltersTemp,
              });
            }}
          />
        </React.Fragment>
      ),
    },
    {
      title: "",
      dataIndex: "action",
      key: "action",
      render: (action, record, groupIndex) => (
        <Popconfirm
          title={"Bạn có muốn xóa không ?"}
          onConfirm={(e) => {
            handleDeletePriceFilter(groupIndex);
          }}
          okText="Có"
          cancelText="Không"
        >
          <Button
            onClick={(e) => {
              e.stopPropagation();
            }}
            danger
            icon={<DeleteOutlined />}
          />
        </Popconfirm>
      ),
    },
  ];
  //#endregion

  const attributeColumns: ColumnsType<ProductCategoryAttributeModel> = [
    {
      title: "Attribute Name",
      dataIndex: "productAttributeId",
      key: "productAttributeId",
      render: (productAttributeId: number, item, index) => (
        <React.Fragment>
          <ProductAttributeSelector
            onChange={(value) => {
              value = value as ProductAttributeModel;
              let attributeTemp = getAttributeTemp();
              attributeTemp[index].productAttributeId = value.id;
              onChange({
                ...model,
                attributeFilters: attributeTemp,
              });
            }}
            isMultiple={false}
            value={productAttributeId}
          />
        </React.Fragment>
      ),
    },
    {
      title: "Filter condition",
      dataIndex: "logicalTerm",
      key: "logicalTerm",
      render: (logicalTerm: LogicalTermEnum, record, index) => (
        <React.Fragment>
          <Select
            className="w-100"
            placeholder="Select filter condition"
            onChange={(value) => {
              let attributeTemp = getAttributeTemp();
              attributeTemp[index].logicalTerm = value;
              onChange({
                ...model,
                attributeFilters: attributeTemp,
              });
            }}
            allowClear
            value={logicalTerm}
            showSearch
            filterOption={(input, option: any) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            <Select.Option value={LogicalTermEnum.And}>Và</Select.Option>
            <Select.Option value={LogicalTermEnum.Or}>Hoặc</Select.Option>
          </Select>
        </React.Fragment>
      ),
    },
    {
      title: "",
      dataIndex: "action",
      key: "action",
      render: (action, record, groupIndex) => (
        <Space>
          <Button
            type="primary"
            onClick={() => {
              handleAddAttributeValue(groupIndex);
            }}
            icon={<PlusCircleOutlined />}
          />
          <Popconfirm
            title={"Bạn có muốn xóa không ?"}
            onConfirm={(e) => {
              handleDeleteAttribute(groupIndex);
            }}
            okText="Có"
            cancelText="Không"
          >
            <Button
              onClick={(e) => {
                e.stopPropagation();
              }}
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];
  const expandedRowAttributeValue = (
    models: ProductCategoryProductAttributeValueModel[],
    attrIndex: number
  ) => {
    const getAttributeValueTemp = () => {
      return [...models];
    };
    const attributeValueColumns: ColumnsType<ProductCategoryProductAttributeValueModel> =
      [
        {
          title: "Attribute Value",
          dataIndex: "valueId",
          key: "valueId",
          render: (valueId: number, record, index) => (
            <React.Fragment>
              <ProductAttributeValueSelector
                onChange={(value) => {
                  value = value as ProductAttributeValueModel;
                  let attributeTemp = getAttributeTemp();
                  let attributeValueTemp = getAttributeValueTemp();
                  attributeValueTemp[index].valueId = value.id;
                  attributeTemp[attrIndex].values = attributeValueTemp;
                  onChange({
                    ...model,
                    attributeFilters: attributeTemp,
                  });
                }}
                isMultiple={false}
                value={valueId}
              />
            </React.Fragment>
          ),
        },
        {
          title: "",
          dataIndex: "action",
          key: "action",
          render: (sort: number, record, index) => (
            <Space>
              <Popconfirm
                title={"Bạn có muốn xóa không ?"}
                onConfirm={(e) => {
                  let attributeTemp = getAttributeTemp();
                  let attributeValueTemp = getAttributeValueTemp();
                  attributeValueTemp.splice(index, 1);
                  attributeTemp[attrIndex].values = attributeValueTemp;
                  onChange({
                    ...model,
                    attributeFilters: attributeTemp,
                  });
                }}
                okText="Có"
                cancelText="Không"
              >
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  danger
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </Space>
          ),
        },
      ];
    return (
      <Table
        dataSource={models && [...models]}
        columns={attributeValueColumns}
        pagination={false}
      ></Table>
    );
  };

  return (
    <div className="product-category-filter-setting">
      <Row gutter={[16, 16]}>
        <Col span={14}>
          <Card title="Filter for attribute">
            <Button
              type="primary"
              className="mb-4"
              onClick={handleAddAttribute}
              icon={<PlusCircleOutlined />}
            >
              Add attribute
            </Button>
            <Table
              expandable={{
                expandedRowRender: (record, index) =>
                  expandedRowAttributeValue(record.values, index),
              }}
              dataSource={memorizedFilterSetting.attributeFilters}
              columns={attributeColumns}
              rowKey={(record, index) => index?.toString() ?? ""}
              pagination={false}
            ></Table>
          </Card>
        </Col>
        <Col span={10}>
          <Card title="Filter for price range">
            <Button
              type="primary"
              className="mb-4"
              onClick={handleAddPriceFilter}
              icon={<PlusCircleOutlined />}
            >
              Add Price Range
            </Button>
            <Table
              dataSource={model.priceFilters}
              columns={priceFilterColumns}
              rowKey={(record, index) => index?.toString() ?? ""}
              pagination={false}
            ></Table>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
